import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { AnimatedIcon } from '../../components/icons';
import { Negotiation, RequestState } from '../../types';

const STATUS_META: Record<string, { bg: string; color: string; label: string }> = {
  [RequestState.NEGOTIATING]: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)', label: '🟡 Negotiating' },
  [RequestState.CONFIRMED]:   { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', label: '🟢 Confirmed'   },
  [RequestState.CLOSED]:      { bg: 'var(--sc-divider)', color: 'var(--sc-text-secondary)', label: '🟢 Closed'      },
};

export const AdminNegotiations: React.FC = () => {
  const [items, setItems]         = useState<Negotiation[]>([]);
  const [total, setTotal]         = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter]       = useState('All');
  const [search, setSearch]       = useState('');
  const [hovered, setHovered]     = useState<string | null>(null);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setIsLoading(true);
    try {
      const statusMap: Record<string, string | undefined> = {
        All:         undefined,
        Negotiating: RequestState.NEGOTIATING,
        Confirmed:   RequestState.CONFIRMED,
        Closed:      RequestState.CLOSED,
      };
      const { data } = await apiClient.get('/admin/negotiations/', {
        params: statusMap[filter] ? { status: statusMap[filter] } : undefined,
      });
      setItems(data.results ?? data ?? []);
      setTotal(data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch {} finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((n) =>
      n.client?.first_name?.toLowerCase().includes(q) ||
      n.client?.last_name?.toLowerCase().includes(q) ||
      n.surveyor?.first_name?.toLowerCase().includes(q) ||
      n.surveyor?.last_name?.toLowerCase().includes(q) ||
      n.request?.service_type?.toLowerCase().includes(q) ||
      n.id.toLowerCase().includes(q)
    );
  }, [items, search]);

  const counts = useMemo(() => ({
    total:       items.length,
    negotiating: items.filter((n) => n.status === RequestState.NEGOTIATING).length,
    confirmed:   items.filter((n) => n.status === RequestState.CONFIRMED).length,
    closed:      items.filter((n) => n.status === RequestState.CLOSED).length,
  }), [items]);

  const kpis = [
    { label: 'Total',       val: counts.total,       color: 'var(--sc-brand-primary)', icon: 'chat'    as const },
    { label: 'Negotiating', val: counts.negotiating, color: 'var(--sc-warning)', icon: 'warning' as const },
    { label: 'Confirmed',   val: counts.confirmed,   color: 'var(--sc-success)', icon: 'success' as const },
    { label: 'Closed',      val: counts.closed,      color: 'var(--sc-text-secondary)', icon: 'check'   as const },
  ];

  const tabs = ['All', 'Negotiating', 'Confirmed', 'Closed'];

  return (
    <div>
      <h1 style={s.title}>Negotiations</h1>
      <p style={s.subtitle}>Monitor and manage all active price negotiations between clients and surveyors.</p>

      {/* KPI Cards */}
      <div style={s.kpiRow}>
        {kpis.map((k) => (
          <div key={k.label} style={{ ...s.kpiCard, borderTop: `3px solid ${k.color}` }}>
            <div style={s.kpiIcon}><AnimatedIcon name={k.icon} size={22} color={k.color} /></div>
            <div style={s.kpiVal}>{k.val}</div>
            <div style={s.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div style={s.filterRow}>
        <div style={s.tabs}>
          {tabs.map((t) => {
            const cnt = t === 'Negotiating' ? counts.negotiating : t === 'Confirmed' ? counts.confirmed : t === 'Closed' ? counts.closed : null;
            return (
              <button key={t} style={{ ...s.tab, ...(filter === t ? s.tabActive : {}) }} onClick={() => setFilter(t)}>
                {t}
                {cnt !== null && (
                  <span style={{ ...s.tabCount, background: filter === t ? 'rgba(255,255,255,0.25)' : 'var(--sc-bg-primary)', color: filter === t ? 'var(--sc-text-inverse)' : 'var(--sc-text-secondary)' }}>
                    {cnt}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={s.searchWrap}>
          <AnimatedIcon name="search" size={15} color="var(--sc-text-tertiary)" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, service, ID…" style={s.searchInput} />
          {search && <button onClick={() => setSearch('')} style={s.clearBtn}>✕</button>}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}><AnimatedIcon name="chat" size={34} color="var(--sc-text-tertiary)" /></div>
          <div style={s.emptyText}>No negotiations found.</div>
          {search && <button style={s.clearLink} onClick={() => setSearch('')}>Clear search</button>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 14, boxShadow: 'var(--sc-shadow-sm)', border: '1px solid var(--sc-border)', marginBottom: 20 }}>
          <div style={{ ...s.table, border: 'none', boxShadow: 'none', borderRadius: 0 }}>
            <div style={s.thead}>
              {[{ label: 'ID', flex: 0.6 }, { label: 'Service', flex: 1.2 }, { label: 'Client', flex: 1.5 }, { label: 'Surveyor', flex: 1.5 }, { label: 'Stat', flex: 1.2 }, { label: 'Date', flex: 1 }, { label: '', flex: 0.4, align: 'center' as const }].map((h, i) => (
                <div key={i} style={{ ...s.th, flex: h.flex, textAlign: h.align }}>{h.label}</div>
              ))}
            </div>
            {filtered.map((n) => {
              const sc = STATUS_META[n.status] || { bg: 'var(--sc-bg-primary)', color: 'var(--sc-text-secondary)', label: n.status };
              const isHov = hovered === n.id;
              return (
                <Link
                  to={`/admin/negotiations/${n.id}`}
                  key={n.id}
                  style={{ ...s.row, background: isHov ? 'var(--sc-bg-hover)' : 'var(--sc-bg-secondary)' }}
                  onMouseEnter={() => setHovered(n.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                <div style={{ ...s.td, flex: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={s.idPill}>#{n.id.slice(0, 8)}</span>
                </div>
                <div style={{ ...s.td, flex: 1.2, fontWeight: 600, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {n.request?.service_type?.replace(/_/g, ' ')}
                </div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.client?.first_name} {n.client?.last_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.client?.phone}</div>
                </div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.surveyor?.first_name} {n.surveyor?.last_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(n.surveyor as any)?.base_city}</div>
                </div>
                <div style={{ ...s.td, flex: 1.2 }}>
                  <span style={{ ...s.badge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                </div>
                <div style={{ ...s.td, flex: 1, color: 'var(--sc-text-tertiary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {new Date(n.created_at).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div style={{ ...s.td, flex: 0.4, textAlign: 'center' as const }}>
                  <span style={{ color: isHov ? 'var(--sc-brand-primary)' : 'var(--sc-text-tertiary)', fontSize: 16, fontWeight: 700, transition: 'color 0.15s' }}>→</span>
                </div>
              </Link>
            );
          })}
          </div>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div style={s.footer}>
          Showing {filtered.length} of {total} negotiation{total !== 1 ? 's' : ''}
          {search ? ` matching "${search}"` : ''}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  title:    { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 24px' },

  kpiRow:  { display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' as const },
  kpiCard: { flex: 1, minWidth: 160, background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '16px 18px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', textAlign: 'center' as const },
  kpiIcon:  { width: 48, height: 48, borderRadius: '50%', background: 'var(--sc-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  kpiVal:   { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },

  filterRow:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const },
  tabs:        { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tab:         { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid var(--sc-border)', borderRadius: 8, background: 'var(--sc-bg-secondary)', fontSize: 13, cursor: 'pointer', fontWeight: 500, color: 'var(--sc-text-secondary)', fontFamily: 'inherit', transition: 'all 0.2s' },
  tabActive:   { background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: '1px solid var(--sc-brand-primary)', fontWeight: 700 },
  tabCount:    { borderRadius: 20, padding: '2px 7px', fontSize: 11, fontWeight: 700 },
  searchWrap:  { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 10, padding: '9px 14px', minWidth: 280, boxShadow: 'var(--sc-shadow-xs)' },
  searchInput: { border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--sc-text-primary)', flex: 1, fontFamily: 'inherit' },
  clearBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sc-text-tertiary)', fontSize: 13, padding: 0, lineHeight: 1 },

  table:  { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 600 },
  thead:  { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th:     { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  row:    { display: 'flex', padding: '14px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center', textDecoration: 'none', transition: 'background 0.15s' },
  td:     { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  idPill: { background: 'var(--sc-bg-primary)', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', fontFamily: 'monospace' },
  badge:  { padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' },

  center:    { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner:   { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty:     { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  emptyIcon: { width: 72, height: 72, borderRadius: '50%', background: 'var(--sc-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' },
  emptyText: { fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--sc-text-primary)' },
  clearLink: { background: 'none', border: 'none', color: 'var(--sc-brand-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' },
  footer:    { marginTop: 12, fontSize: 12, color: 'var(--sc-text-tertiary)', textAlign: 'center' as const },
};
